import React, { FC, useCallback, useRef, useState } from 'react';
import { DateField } from '@mui/x-date-pickers/DateField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DATE_FORMAT, setClass, useOutsideClick } from '@utils';
import { add, format, sub } from 'date-fns';
import { ru } from 'date-fns/locale';
import { CalendarPreview } from '../calendarPreview';
import { DateBlockAdapter } from '../dateBlockAdapter';
import { ruRU } from '@mui/x-date-pickers/locales';
import '../../styles/components/datePicker.scss';

interface DatePickerProp {
  value: Date;
  minDate?: Date;
  maxDate?: Date;
  disablePast?: boolean;
  readOnly?: boolean;
  classNameInput?: string;
  disabled?: boolean;
  inputFormat?: string;
  isPlaceholder?: boolean;
  checkIsChoosedDay?: (day: Date) => boolean;
  onChange: (newValue: Date) => void;
  error?: boolean;
  shiftPopup?: boolean;
}

export const DatePicker: FC<DatePickerProp> = ({
  value, minDate, maxDate, disablePast = true, readOnly, 
  classNameInput, disabled, inputFormat = 'dd MMM yyyy', 
  isPlaceholder = false, checkIsChoosedDay, onChange, error, 
  shiftPopup
}) => {
  const [openPopup, setOpenPopup] = useState(false);
  const [startDate, setStartDate] = useState(value ?? new Date());

  const moveToNextMonth = useCallback(() => {
    const newCalendarData = add(startDate, { months: 1 });
    setStartDate(newCalendarData);
  }, [startDate]);

  const moveToPrevMonth = useCallback(() => {
    const newCalendarData = sub(startDate, { months: 1 });
    setStartDate(newCalendarData);
  }, [startDate]);

  const handleChange = useCallback((day: Date) => {
    onChange(day);
    setStartDate(day);
    close();
  }, []);

  const open = useCallback(() => {
    !readOnly && !disabled && setOpenPopup(true);
    setStartDate(value);
  }, [readOnly, disabled, value]);

  const close = useCallback(() => setOpenPopup(false), []);

  const getDayClassName = useCallback((day: Date) =>
    (format(value, DATE_FORMAT) === format(day, DATE_FORMAT) || checkIsChoosedDay?.(day)) && 'choosed-day'
    , [value, checkIsChoosedDay]);

  const inputRef = useRef();
  const ref = useOutsideClick(close);


  return (
    <div className='date-picker'>
      <LocalizationProvider 
        adapterLocale={ru} 
        dateAdapter={DateBlockAdapter}
        localeText={{...ruRU.components.MuiLocalizationProvider.defaultProps.localeText, fieldMonthPlaceholder: () => 'ММ'}}
      >
        <DateField
          value={value}
          onChange={handleChange}
          ref={inputRef}
          className={setClass('date-picker-input', classNameInput, isPlaceholder && 'placeholder')}
          format={inputFormat}
          maxDate={maxDate}
          minDate={minDate}
          onClick={open}
          readOnly={readOnly}
          disablePast={disablePast}
          disabled={disabled}
          slotProps={{
            textField: {
              error,
            },
          }}
        />
        {openPopup && (
          <div className={setClass('popup', shiftPopup && 'shift-popup')}>
            <CalendarPreview
              ref={ref}
              startDate={startDate}
              minDate={minDate}
              maxDate={maxDate}
              disablePast={disablePast}
              getDayClassName={getDayClassName}
              moveToPrevMonth={moveToPrevMonth}
              moveToNextMonth={moveToNextMonth}
              onClickDay={handleChange}
              isNeedMainWorkCalendar={false}
            />
          </div>
        )}
      </LocalizationProvider>
    </div>
  );
};
